import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createRecognitionAndReview,
  updateRecognitionAndReview,
} from 'api/recognitions.service';
import { generateRecognitionRequestPayload } from 'hooks/helpers';
import { useToast } from 'hooks/ui';
import { Recognition } from 'models/recognition.model';
import { setDraftRecognition } from 'store/recognition';
import { useApplicationStore } from 'store/application';
import { recognitionRoutes } from 'router/recognitionRoutes';
import { recognitionKeys } from './queryKeys';

export const useRecognitionConditionalReviewQuery = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setLoading, removeLoading } = useApplicationStore((state) => state);

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: { recognition: Recognition; new: boolean }) => {
      const draftRecognition = generateRecognitionRequestPayload(
        payload.recognition,
      );

      setLoading();
      if (payload.recognition.reward === 'PICTURE') {
        if (payload.new) {
          return createRecognitionAndReview(
            {
              ...draftRecognition,
              attachments: [],
            },
            payload.recognition.attachments,
          );
        }

        return updateRecognitionAndReview({
          id: payload.recognition.id,
          ...draftRecognition,
          attachments: payload.recognition.attachments,
        });
      }

      if (payload.new) {
        return createRecognitionAndReview(draftRecognition);
      }

      return updateRecognitionAndReview({
        id: payload.recognition.id,
        ...draftRecognition,
      });
    },
    onSuccess: (recognition) => {
      toast.success('Recognition has been sent for review successfully');
      navigate(recognitionRoutes.recognitionId(recognition.id));
      setDraftRecognition(recognition);
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: recognitionKeys.all });
      removeLoading();
    },
  });

  return { isLoading: isPending, mutate };
};
